/**
 * 供需对接平台页面
 * 包含专利池和需求发布功能
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useListApi } from '../../../hooks/useApi';
import showcaseService from '../../../services/api/showcaseService';
import demandService, { DemandItem } from '../../../services/api/demandService';
import { getCategoryLabel, getIndustryLabel, getTechnologyLabel } from '../../../utils/dictConverter';
import { navigateTo } from '../../../utils/router';
import './DemandPage.css';

const DemandPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // 当前标签页：'patent-pool' | 'demand'
  // 从 URL 参数初始化，如果没有则默认为 'patent-pool'
  const [activeTab, setActiveTab] = useState<'patent-pool' | 'demand'>(() => {
    const tabParam = searchParams.get('tab');
    return (tabParam === 'demand' ? 'demand' : 'patent-pool') as 'patent-pool' | 'demand';
  });
  
  // 当 URL 参数变化时，更新 activeTab
  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'demand') {
      setActiveTab('demand');
    } else if (tabParam === 'patent-pool' || !tabParam) {
      setActiveTab('patent-pool');
    }
  }, [searchParams]);
  
  // 专利池相关状态
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '专利池');
  const [patentPoolConditions, setPatentPoolConditions] = useState({
    title: '',
    company: '',
    industry: '',
    technology: ''
  });
  
  // 需求发布相关状态
  const [demandType, setDemandType] = useState<'supply' | 'demand' | 'all'>('all');
  const [demandKeyword, setDemandKeyword] = useState('');
  
  // 获取专利池数据（只获取专利池分类的数据）
  const patentPoolApi = useListApi(
    async (params) => {
      const result = await showcaseService.getShowcaseList({ 
        ...params, 
        current: 1,
        size: 1000,
        category: 'patent_pool'
      });
      
      const apiData = result as any;
      let rows: any[] = [];
      let total = 0;
      
      if (apiData.records && Array.isArray(apiData.records)) {
        rows = apiData.records;
        total = apiData.total || apiData.records.length;
      } else if (apiData.rows && Array.isArray(apiData.rows)) {
        rows = apiData.rows;
        total = apiData.total || apiData.rows.length;
      } else if (apiData.data && Array.isArray(apiData.data)) {
        rows = apiData.data;
        total = apiData.total || apiData.data.length;
      } else if (Array.isArray(apiData)) {
        rows = apiData;
        total = apiData.length;
      }
      
      return { data: { rows, total } };
    },
    { pageSize: 1000 }
  );
  
  // 在组件挂载时加载专利池数据
  useEffect(() => {
    if (activeTab === 'patent-pool') {
      patentPoolApi.execute();
    }
  }, [activeTab]);
  
  // 需求发布数据状态
  const [demandItems, setDemandItems] = useState<DemandItem[]>([]);
  const [demandLoading, setDemandLoading] = useState(false);
  const [demandTotal, setDemandTotal] = useState(0);
  
  // 加载需求发布数据
  const loadDemandData = async () => {
    if (activeTab !== 'demand') return;
    
    setDemandLoading(true);
    try {
      const result = await demandService.getList({
        current: 1,
        size: 100,
        demandType: demandType !== 'all' ? demandType : undefined,
        keyword: demandKeyword || undefined,
        status: '1' // 只获取已发布的需求
      });
      
      const apiData = result as any;
      let rows: any[] = [];
      let total = 0;
      
      if (apiData.data?.records && Array.isArray(apiData.data.records)) {
        rows = apiData.data.records;
        total = apiData.data.total || apiData.data.records.length;
      } else if (apiData.data?.rows && Array.isArray(apiData.data.rows)) {
        rows = apiData.data.rows;
        total = apiData.data.total || apiData.data.rows.length;
      } else if (apiData.records && Array.isArray(apiData.records)) {
        rows = apiData.records;
        total = apiData.total || apiData.records.length;
      } else if (Array.isArray(apiData)) {
        rows = apiData;
        total = apiData.length;
      }
      
      setDemandItems(rows);
      setDemandTotal(total);
    } catch (error: any) {
      console.error('加载需求数据失败:', error);
      setDemandItems([]);
      setDemandTotal(0);
    } finally {
      setDemandLoading(false);
    }
  };
  
  // 当切换到需求发布标签或筛选条件变化时，加载数据
  useEffect(() => {
    if (activeTab === 'demand') {
      loadDemandData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, demandType, demandKeyword]);
  
  // 专利池数据
  const patentPoolItems = useMemo(() => {
    if (!patentPoolApi.data || !patentPoolApi.data.rows) return [];
    return patentPoolApi.data.rows;
  }, [patentPoolApi.data]);
  
  // 需求发布数据已在上面定义
  
  // 过滤专利池数据
  const filteredPatentPoolItems = useMemo(() => {
    return patentPoolItems.filter((item: any) => {
      // 项目名称匹配
      const matchesTitle = !patentPoolConditions.title || !patentPoolConditions.title.trim() ||
        item.title.toLowerCase().includes(patentPoolConditions.title.trim().toLowerCase());
      
      // 企业名称匹配
      const matchesCompany = !patentPoolConditions.company || !patentPoolConditions.company.trim() ||
        (item.companyName?.toLowerCase().includes(patentPoolConditions.company.trim().toLowerCase()) ||
         item.company?.toLowerCase().includes(patentPoolConditions.company.trim().toLowerCase()));
      
      // 所属行业匹配
      const matchesIndustry = !patentPoolConditions.industry || !patentPoolConditions.industry.trim() || (() => {
        const conditionIndustry = patentPoolConditions.industry.trim().toLowerCase();
        const itemIndustryLabel = getIndustryLabel(item.industry).toLowerCase();
        const itemIndustry = (item.industry || '').toLowerCase();
        
        return itemIndustryLabel === conditionIndustry ||
               itemIndustryLabel.includes(conditionIndustry) ||
               conditionIndustry.includes(itemIndustryLabel) ||
               itemIndustry.includes(conditionIndustry) ||
               conditionIndustry.includes(itemIndustry);
      })();
      
      // 核心技术匹配
      const matchesTechnology = !patentPoolConditions.technology || !patentPoolConditions.technology.trim() || (() => {
        const conditionTech = patentPoolConditions.technology.trim().toLowerCase();
        
        if (item.technology) {
          if (typeof item.technology === 'string') {
            const techArray = item.technology.split(',').map((t: string) => t.trim()).filter(Boolean);
            for (const tech of techArray) {
              const techLabel = getTechnologyLabel(tech).toLowerCase();
              if (techLabel === conditionTech || 
                  tech.toLowerCase() === conditionTech ||
                  techLabel.includes(conditionTech) ||
                  conditionTech.includes(techLabel)) {
                return true;
              }
            }
          }
        }
        
        if (item.keywords && Array.isArray(item.keywords)) {
          for (const keyword of item.keywords) {
            if (keyword.toLowerCase().includes(conditionTech) || 
                conditionTech.includes(keyword.toLowerCase())) {
              return true;
            }
          }
        }
        
        return false;
      })();
      
      return matchesTitle && matchesCompany && matchesIndustry && matchesTechnology;
    });
  }, [patentPoolItems, patentPoolConditions]);
  
  // 处理专利池条件检索
  const handlePatentPoolConditionChange = (field: string, value: string) => {
    setPatentPoolConditions(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  // 处理需求发布搜索
  const handleDemandSearch = () => {
    loadDemandData();
  };
  
  return (
    <div className="demand-page">
      {/* 标签页切换 */}
      <div className="demand-tabs">
        <button 
          className={`tab-btn ${activeTab === 'patent-pool' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('patent-pool');
            const params = new URLSearchParams(searchParams);
            params.set('tab', 'patent-pool');
            setSearchParams(params);
          }}
        >
          专利池
        </button>
        <button 
          className={`tab-btn ${activeTab === 'demand' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('demand');
            const params = new URLSearchParams(searchParams);
            params.set('tab', 'demand');
            setSearchParams(params);
          }}
        >
          需求发布
        </button>
      </div>
      
      {/* 专利池内容 */}
      {activeTab === 'patent-pool' && (
        <div className="patent-pool-content">
          {/* 搜索条件 */}
          <div className="patent-pool-search">
            <div className="search-row">
              <div className="search-field">
                <label>项目名称：</label>
                <input
                  type="text"
                  value={patentPoolConditions.title}
                  onChange={(e) => handlePatentPoolConditionChange('title', e.target.value)}
                  placeholder="请输入项目名称"
                />
              </div>
              <div className="search-field">
                <label>企业名称：</label>
                <input
                  type="text"
                  value={patentPoolConditions.company}
                  onChange={(e) => handlePatentPoolConditionChange('company', e.target.value)}
                  placeholder="请输入企业名称"
                />
              </div>
            </div>
            <div className="search-row">
              <div className="search-field">
                <label>所属行业：</label>
                <input
                  type="text"
                  value={patentPoolConditions.industry}
                  onChange={(e) => handlePatentPoolConditionChange('industry', e.target.value)}
                  placeholder="请输入所属行业"
                />
              </div>
              <div className="search-field">
                <label>核心技术：</label>
                <input
                  type="text"
                  value={patentPoolConditions.technology}
                  onChange={(e) => handlePatentPoolConditionChange('technology', e.target.value)}
                  placeholder="请输入核心技术"
                />
              </div>
            </div>
          </div>
          
          {/* 专利池列表 */}
              <div className="patent-pool-list">
                {patentPoolApi.loading ? (
                  <div className="loading">加载中...</div>
                ) : filteredPatentPoolItems.length === 0 ? (
                  <div className="empty">暂无数据</div>
                ) : (
                  <div className="patent-pool-grid">
                    {filteredPatentPoolItems.map((item: any) => (
                      <div
                        key={item.id}
                        className="patent-pool-item"
                        onClick={() => navigateTo.showcaseDetail(navigate, item.id, { from: 'demand' })}
                      >
                        <div className="patent-item-body">
                          <div className="patent-item-header">
                            <h3 className="patent-item-title" title={item.title}>
                              {item.title}
                            </h3>
                            <div className="patent-item-badge">专利池</div>
                          </div>
                          <p
                            className="patent-description"
                            title={item.description}
                          >
                            {item.description}
                          </p>
                          <div className="patent-item-meta">
                            {item.companyName && (
                              <div className="patent-meta-row" title={item.companyName}>
                                <span className="patent-meta-label">企业：</span>
                                <span className="patent-meta-value">{item.companyName}</span>
                              </div>
                            )}
                            {item.industry && (
                              <div className="patent-meta-row" title={getIndustryLabel(item.industry)}>
                                <span className="patent-meta-label">行业：</span>
                                <span className="patent-meta-value">
                                  {getIndustryLabel(item.industry)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
        </div>
      )}
      
      {/* 需求发布内容 */}
      {activeTab === 'demand' && (
        <div className="demand-content">
          {/* 搜索和发布按钮 */}
          <div className="demand-header">
            <div className="demand-search">
              <input
                type="text"
                value={demandKeyword}
                onChange={(e) => setDemandKeyword(e.target.value)}
                placeholder="搜索需求..."
                onKeyPress={(e) => e.key === 'Enter' && handleDemandSearch()}
              />
              <button onClick={handleDemandSearch}>搜索</button>
            </div>
            <div className="demand-filters">
              <button 
                className={demandType === 'all' ? 'active' : ''}
                onClick={() => setDemandType('all')}
              >
                全部
              </button>
              <button 
                className={demandType === 'supply' ? 'active' : ''}
                onClick={() => setDemandType('supply')}
              >
                供应
              </button>
              <button 
                className={demandType === 'demand' ? 'active' : ''}
                onClick={() => setDemandType('demand')}
              >
                需求
              </button>
            </div>
          </div>
          
          {/* 需求列表 */}
          <div className="demand-list">
            {demandLoading ? (
              <div className="loading">加载中...</div>
            ) : demandItems.length === 0 ? (
              <div className="empty">暂无数据</div>
            ) : (
              <div className="demand-grid">
                {demandItems.map((item: DemandItem) => (
                  <div 
                    key={item.id} 
                    className="demand-item"
                    onClick={() => navigate(`/show/demand/${item.id}?tab=demand`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="demand-type-badge">
                      {item.demandType === 'supply' ? '供应' : '需求'}
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                    {item.companyName && <p className="company">企业：{item.companyName}</p>}
                    {item.contactPerson && <p className="contact">联系人：{item.contactPerson}</p>}
                    {item.contactPhone && <p className="phone">电话：{item.contactPhone}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DemandPage;

